/**
 * CypAdhdStructuredRiskPanel.js
 *
 * TOC's NEST-parity structured Risk Assessment for the CYP Clinical
 * Interview (cypAdhdFlow.riskAssessment === 'structuredV1'). Rendered by
 * 1_CypAdhdClinicalInterview.js in place of the standard risk panel; content
 * comes from cypAdhdStructuredRiskConfig.js verbatim.
 *
 * Data shape (persisted wholesale under the CI stage doc's
 * structuredRiskAssessment field — replace semantics, no deep-merge, because
 * detail selections are arrays):
 *   {
 *     categories: { [categoryId]: { present: bool|null, details: [..] } },
 *       (older records may also hold a per-category `notes` string from the
 *       retired "Further details" box; it is kept but no longer shown)
 *     clinicalNotes: '' | [{ id, text, type }],
 *       a plain string on records written before the bullet-note editor;
 *       the editor shows that string as the first note and saves an array
 *       the next time the notes change,
 *     acknowledgement: {
 *       acknowledged: bool, acknowledgedAt: ISO string,
 *       acknowledgedBy: uid, acknowledgedByName: '',
 *       riskIndicators: [labels at acknowledgement time],
 *     } | null,
 *   }
 *
 * The For Consideration notice appears while any risk-indicator category is
 * Yes (protective factors never trigger it) and asks for an explicit
 * acknowledgement. Recording facts only — nothing here computes a clinical
 * conclusion.
 */
import React, { useMemo } from 'react';
import { ExclamationIcon } from '@heroicons/react/solid';
import { useAuth } from '../../../../hooks/useAuth';
import AutoSaveCreatable from '../../../../components/AutoSaveCreatable';
import getScrollablePortalTarget from '../../../../utils/getScrollablePortalTarget';
import StructuredNotesEditor from '../../12_observation/components/StructuredNotesEditor';
import { normaliseNotesValue } from '../../../../utils/autismReviewData';
import { CYP_NOTE_TAGGING_ENABLED, CYP_NOTE_TAG_OPTIONS } from '../../../../constants/cypAdhdNoteTagOptions';
// Shared with the medical screening panel; the pill pair itself is unchanged.
import YesNoToggle from './YesNoToggle';
import {
  CYP_STRUCTURED_RISK_TOOLTIP,
  CYP_STRUCTURED_RISK_NOTICE,
  CYP_STRUCTURED_RISK_SECTIONS,
  listYesRiskIndicators,
  isAcknowledgementCurrent,
} from '../config/cypAdhdStructuredRiskConfig';

export const EMPTY_STRUCTURED_RISK = Object.freeze({
  categories: {},
  clinicalNotes: '',
  acknowledgement: null,
});

export function normalizeStructuredRiskData(stored) {
  if (!stored || typeof stored !== 'object') return { ...EMPTY_STRUCTURED_RISK, categories: {} };
  const normalized = {
    categories: { ...(stored.categories || {}) },
    clinicalNotes: stored.clinicalNotes || '',
    acknowledgement: stored.acknowledgement || null,
  };
  // The "Actions taken / escalation" box was removed (Sai, 7 Oct: actions and
  // escalation are recorded off-platform). Text typed into it before then is
  // carried through untouched rather than erased by the wholesale save.
  if (stored.actionsTaken) normalized.actionsTaken = stored.actionsTaken;
  return normalized;
}

/** True when the clinical-notes field holds anything a clinician typed,
    whether that is the legacy plain string or the bullet-note array. */
export function hasClinicalNotesContent(notes) {
  if (typeof notes === 'string') return Boolean(notes.trim());
  if (Array.isArray(notes)) {
    return notes.some((note) => note && typeof note.text === 'string' && note.text.trim());
  }
  return false;
}

/**
 * Notes in the shape StructuredNotesEditor renders.
 *
 * A string left by the old textarea becomes one bullet, so text already on
 * the record stays visible. It is only rewritten as an array when the
 * clinician next edits the notes.
 */
function clinicalNotesForEditor(raw) {
  if (Array.isArray(raw)) return normaliseNotesValue(raw);
  if (typeof raw === 'string' && raw.trim()) {
    return [{ id: 'legacy-clinical-notes', text: raw.trim(), type: 'note' }];
  }
  return [];
}

/** True when anything at all has been recorded (drives step completion). */
export function hasAnyStructuredRiskInput(data) {
  if (!data) return false;
  const anyCategory = Object.values(data.categories || {}).some(
    (c) => c && (
      c.present === true
      || c.present === false
      || (Array.isArray(c.details) && c.details.length > 0)
    ),
  );
  return anyCategory || hasClinicalNotesContent(data.clinicalNotes);
}

/** Card surface — the autism History section panel, shared with the CI page
    and the MSE panel so the three step types read as one module. */
const PANEL_CLASS = 'bg-gray-50 rounded-lg border border-gray-300 text-left p-6 shadow-sm';

/** The For Consideration banner. The Risk step's forward button scrolls
    this into view when a Yes has not yet been acknowledged. */
export const CYP_RISK_NOTICE_ID = 'cyp-structured-risk-notice';

// Same menu styling as the MSE details cell, so a risk detail and an MSE
// detail read as one kind of control.
const SELECT_PORTAL_PROPS = {
  classNamePrefix: 'select',
  menuPortalTarget: getScrollablePortalTarget(),
  menuPosition: 'fixed',
};
const SELECT_STYLES = {
  menu: (base) => ({ ...base, textAlign: 'left' }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    textAlign: 'left',
    backgroundColor: state.isSelected ? 'lightgray' : base.backgroundColor,
  }),
};
const toOption = (value) => ({ value, label: value });
const detailsPlaceholder = (canEdit) => (canEdit
  ? 'Select options or write your own..'
  : 'Selection can only be made by assigned clinician');

function DetailChips({
  options, selected = [], onToggle, disabled, categoryLabel,
  legend = 'Details — select all that apply',
}) {
  return (
    <fieldset className="mt-3">
      <legend className="text-xs font-medium text-gray-500 mb-2">
        {legend}
      </legend>
      <div className="flex flex-wrap gap-2" aria-label={`${categoryLabel} details`}>
        {options.map((option) => {
          const isSelected = selected.includes(option);
          return (
            <button
              key={option}
              type="button"
              disabled={disabled}
              onClick={() => onToggle(option)}
              aria-pressed={isSelected}
              className={`px-3 py-1.5 min-h-[36px] text-sm font-semibold rounded-full border transition-all duration-200 ${
                isSelected
                  ? 'bg-primary/10 border-primary text-primary'
                  : 'bg-white border-gray-400 text-gray-800 hover:bg-gray-50'
              } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              {option}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

export default function CypAdhdStructuredRiskPanel({
  riskData,
  onRiskDataChange,
  canEdit,
}) {
  const { user } = useAuth();
  const data = riskData || EMPTY_STRUCTURED_RISK;
  const categories = data.categories || {};

  // The live list of Yes risk indicators (protective factors excluded) —
  // drives the For Consideration notice and its acknowledgement snapshot.
  const yesRiskIndicators = useMemo(
    () => listYesRiskIndicators(categories),
    [categories],
  );
  const showNotice = yesRiskIndicators.length > 0;
  const acknowledged = isAcknowledgementCurrent(data.acknowledgement, yesRiskIndicators);

  const updateCategory = (categoryId, patch) => {
    if (!canEdit) return;
    const existing = categories[categoryId] || { present: null, details: [], notes: '' };
    const nextCategories = {
      ...categories,
      [categoryId]: { ...existing, ...patch },
    };
    onRiskDataChange({
      ...data,
      categories: nextCategories,
      acknowledgement: isAcknowledgementCurrent(
        data.acknowledgement,
        listYesRiskIndicators(nextCategories),
      )
        ? data.acknowledgement
        : null,
    });
  };

  const handlePresentChange = (categoryId, clicked) => {
    const existing = categories[categoryId] || {};
    // Adult ADHD Risk Assessment (11_RSK.js): clicking the button that is
    // already on clears it back to unanswered. A first click on Yes or No
    // still records that answer.
    const nextPresent = existing.present === clicked ? null : clicked;
    // Listed details belong to the answer. A No or a clear drops them, the
    // same way the adult page drops its predefined options. Anything the
    // clinician typed that is not in the list is kept, as the adult page
    // keeps those custom entries.
    const listed = new Set(
      CYP_STRUCTURED_RISK_SECTIONS
        .flatMap((section) => section.categories)
        .find((cat) => cat.id === categoryId)?.options || [],
    );
    const customDetails = (existing.details || []).filter((detail) => !listed.has(detail));
    updateCategory(categoryId, {
      present: nextPresent,
      details: nextPresent === true ? (existing.details || []) : customDetails,
      notes: nextPresent === true ? (existing.notes || '') : '',
    });
  };

  /**
   * "No to all?" — answer No to every category in one section at once.
   *
   * Ported from the adult ADHD Risk Assessment (6_clinicalInterview/11_RSK.js,
   * executeNoToAll): most children have nothing to record in most sections,
   * and six clicks per section is the kind of friction that makes clinicians
   * skip the form. Behaviour matches the adult page exactly:
   *   - every category in the section becomes present: false;
   *   - each category's details take the "No" default. The adult page's No
   *     default is its noOptions sentinel ("None"); this specification has no
   *     per-category No options, so the No default here is the same as a
   *     single No click — the details dropdown is cleared (a chosen detail
   *     under a No would contradict the answer in the record);
   *   - one write for the whole section, so the record can never be left
   *     half-updated if the save fails part-way;
   *   - the acknowledgement is re-evaluated through the same rule as any
   *     other category change (isAcknowledgementCurrent). Nos only ever
   *     shrink the Yes list, so a current acknowledgement stays current —
   *     the outcome is identical to clicking No on each category in turn.
   * The button is disabled when the viewer cannot edit or when every
   * category in the section is already No, so it never looks available
   * while doing nothing (adult behaviour).
   */
  const handleNoToAll = (sectionDef) => {
    if (!canEdit) return;
    const nextCategories = { ...categories };
    sectionDef.categories.forEach((cat) => {
      const existing = categories[cat.id] || {};
      nextCategories[cat.id] = { ...existing, present: false, details: [], notes: '' };
    });
    onRiskDataChange({
      ...data,
      categories: nextCategories,
      acknowledgement: isAcknowledgementCurrent(
        data.acknowledgement,
        listYesRiskIndicators(nextCategories),
      )
        ? data.acknowledgement
        : null,
    });
  };

  /**
   * Protective factors have no Yes/No. A ticked factor is recorded as
   * present; clearing the last one leaves the category unanswered, which is
   * how "none identified" is stored. There is deliberately no No, and no
   * "No to all?", because a bulk No here would record that the child has no
   * protective factors at all.
   */
  const handleProtectiveDetailToggle = (categoryId, option) => {
    const existing = categories[categoryId] || { details: [] };
    const details = existing.details || [];
    const nextDetails = details.includes(option)
      ? details.filter((d) => d !== option)
      : [...details, option];
    updateCategory(categoryId, {
      present: nextDetails.length > 0 ? true : null,
      details: nextDetails,
    });
  };

  /**
   * Details dropdown (the MSE cell, not a row of buttons). The category's
   * listed options are offered, and a typed entry is kept too, the same way
   * the MSE details box keeps something the clinician wrote that was not in
   * the list. Choosing a detail does not itself answer Yes or No.
   */
  const handleDetailsChange = (categoryId, selected) => {
    updateCategory(categoryId, {
      details: (selected || []).map((option) => option.value),
    });
  };

  const handleAcknowledgementChange = (checked) => {
    if (!canEdit) return;
    onRiskDataChange({
      ...data,
      acknowledgement: checked
        ? {
            acknowledged: true,
            acknowledgedAt: new Date().toISOString(),
            acknowledgedBy: user?.uid || null,
            acknowledgedByName: user?.displayName || '',
            riskIndicators: yesRiskIndicators,
          }
        : null,
    });
  };

  return (
    <div className="space-y-6 text-left">
      {/* Orientation note, written as plain text above the cards — the same
          treatment as the MSE introduction. A bordered box made this read as
          a second panel rather than the page's own explanation. */}
      <div className="text-gray-700 space-y-2">
        <p>{CYP_STRUCTURED_RISK_TOOLTIP}</p>
      </div>

      {/* For Consideration — always mounted so it can slide open and closed
          the way the adult ADHD risk banner does (11_RSK.js). Red, with an
          icon and a heading, so the warning is not carried by colour alone.
          The open height is taller than the adult banner because this one
          also holds the acknowledgement checkbox and the indicator list;
          a short max-height would clip the box the clinician has to tick.
          When nothing is Yes the shell collapses to no height and drops its
          own gap, so the page does not keep a blank stripe. */}
      <div
        id={CYP_RISK_NOTICE_ID}
        data-testid="risk-banner-shell"
        aria-hidden={!showNotice}
        className={`overflow-hidden transition-all duration-400 ease-in-out ${
          showNotice ? 'max-h-[60rem] opacity-100' : 'max-h-0 opacity-0 !mt-0'
        }`}
      >
        <div className="bg-red-50 rounded-2xl p-4 text-left" role={showNotice ? 'alert' : undefined}>
          <div className="flex items-start">
            <ExclamationIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div className="ml-3 space-y-2">
              <h3 className="text-sm font-medium text-red-800">
                {CYP_STRUCTURED_RISK_NOTICE.title}
              </h3>
              <p className="text-sm text-red-700">
                {CYP_STRUCTURED_RISK_NOTICE.selectedPrefix}
              </p>
              <ul className="list-disc pl-5 text-sm text-red-700">
                {yesRiskIndicators.map((label) => (
                  <li key={label}>{label}</li>
                ))}
              </ul>
              <p className="text-sm text-red-700">
                {CYP_STRUCTURED_RISK_NOTICE.guidance}
              </p>
              <p className="text-sm text-red-700">
                {CYP_STRUCTURED_RISK_NOTICE.platformNote}
              </p>
              <label className="flex items-start gap-2 pt-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  disabled={!canEdit}
                  onChange={(e) => handleAcknowledgementChange(e.target.checked)}
                  className="mt-0.5 h-5 w-5 text-primary border-gray-300 rounded focus:ring-primary"
                />
                <span className="text-sm font-medium text-red-800">
                  {CYP_STRUCTURED_RISK_NOTICE.acknowledgementLabel}
                </span>
              </label>
              {acknowledged && data.acknowledgement?.acknowledgedAt && (
                <p className="text-xs text-red-700">
                  Acknowledged
                  {data.acknowledgement.acknowledgedByName
                    ? ` by ${data.acknowledgement.acknowledgedByName}`
                    : ''}
                  {' on '}
                  {new Date(data.acknowledgement.acknowledgedAt).toLocaleString('en-GB')}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Clinical notes first (UAT feedback, 8 Oct): the clinician writes
          narrative observations at the top, then works through the structured
          categories below. Notes come first so they can be drafted during the
          conversation before the categorical recording starts. */}
      <section className={PANEL_CLASS}>
        <h4 className="font-bold text-lg mb-2">Clinical notes</h4>
        <p className="text-sm text-gray-600 mb-4">
          Please document any relevant risk information, recent incidents,
          contextual factors, protective factors, clinical formulation or
          other observations.
        </p>
        {/* The same bullet-note editor as the MSE Clinical Notes card: one
            point per Enter. A note typed into the old textarea is shown as
            the first bullet until it is next edited. */}
        <StructuredNotesEditor
          notes={clinicalNotesForEditor(data.clinicalNotes)}
          onChange={(notes) => canEdit && onRiskDataChange({ ...data, clinicalNotes: notes })}
          domainId="structured-risk-clinical-notes"
          placeholder="Add a note, then press Enter to save each point"
          enableCriteriaTagging={CYP_NOTE_TAGGING_ENABLED}
          tagOptions={CYP_NOTE_TAG_OPTIONS}
          readOnly={!canEdit}
        />
      </section>

      {CYP_STRUCTURED_RISK_SECTIONS.map((sectionDef, sectionIndex) => {
        const heading = `${sectionIndex + 1}. ${sectionDef.title}`;

        // Protective factors are strengths, not a risk to rule out. They are
        // always listed, with no Yes/No and no "No to all?": not ticking any
        // means none were identified (or they were written in Clinical notes).
        if (sectionDef.isProtectiveFactors) {
          return (
            <section key={sectionDef.id} className={PANEL_CLASS}>
              <h4 className="font-bold text-lg mb-2">{heading}</h4>
              <p className="text-sm text-gray-600 mb-2">
                Select all that apply. Leave blank if none were identified.
              </p>
              {sectionDef.categories.map((cat) => (
                <DetailChips
                  key={cat.id}
                  options={cat.options}
                  selected={(categories[cat.id] || {}).details || []}
                  onToggle={(option) => handleProtectiveDetailToggle(cat.id, option)}
                  disabled={!canEdit}
                  categoryLabel={cat.label}
                  legend="Select all that apply"
                />
              ))}
            </section>
          );
        }

        // Disable the bulk action when every category in this section is
        // already No (adult 11_RSK.js behaviour).
        const allAlreadyNo = sectionDef.categories.length > 0 && sectionDef.categories.every(
          (cat) => categories[cat.id]?.present === false,
        );
        const noToAllDisabled = !canEdit || allAlreadyNo;

        return (
          <section key={sectionDef.id} className={PANEL_CLASS}>
            {/* MSE-style header: title over Category, "No to all?" centred
                over Present?, and the details dropdown in the third column. */}
            <div className="flex justify-between items-center mb-4">
              <div className="w-[30%] min-w-0">
                <h4 className="font-bold text-lg">{heading}</h4>
              </div>
              <div className="w-[20%] flex justify-center">
                <button
                  type="button"
                  onClick={() => handleNoToAll(sectionDef)}
                  aria-label={`No to all in ${sectionDef.title}`}
                  className={`py-1.5 px-3 rounded-md shadow-sm font-semibold text-xs text-gray-900 bg-white border border-gray-400 transition-all duration-100 ${
                    noToAllDisabled
                      ? 'opacity-60 cursor-not-allowed'
                      : 'hover:bg-red-700 hover:text-white hover:border-red-700'
                  }`}
                  disabled={noToAllDisabled}
                >
                  No to all?
                </button>
              </div>
              <div className="w-[50%]" aria-hidden="true" />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full table-auto min-w-[640px]">
                <thead>
                  <tr className="text-sm text-gray-600 border-t border-gray-200">
                    <th className="text-left font-semibold py-2 w-[30%]">Category</th>
                    <th className="text-center font-semibold py-2 w-[20%]">Present?</th>
                    <th className="text-left font-semibold py-2 w-[50%]">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {sectionDef.categories.map((cat) => {
                    const catData = categories[cat.id] || {};
                    return (
                      <tr key={cat.id} className="border-t border-gray-200">
                        <td className="py-3 pr-4 align-top">
                          <span className="text-sm font-semibold text-gray-800">{cat.label}</span>
                        </td>
                        <td className="py-3 text-center align-top">
                          <YesNoToggle
                            value={catData.present ?? null}
                            onChange={(present) => handlePresentChange(cat.id, present)}
                            disabled={!canEdit}
                            categoryLabel={cat.label}
                          />
                        </td>
                        <td className="py-3 align-top">
                          <AutoSaveCreatable
                            isMulti
                            aria-label={`${cat.label} details`}
                            isDisabled={!canEdit}
                            styles={SELECT_STYLES}
                            options={(cat.options || []).map(toOption)}
                            value={(catData.details || []).map(toOption)}
                            onChange={(selected) => handleDetailsChange(cat.id, selected)}
                            formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                            placeholder={detailsPlaceholder(canEdit)}
                            className="basic-multi-select text-sm"
                            {...SELECT_PORTAL_PROPS}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
    </div>
  );
}
